<template>
  <div class="ai-design-chat-wrapper">
    <!-- Toggle button -->
    <button
      v-if="!isOpen"
      class="ai-toggle-btn"
      @click="isOpen = true"
      title="AI-avustaja"
    >
      <span class="icon">
        <i class="fas fa-magic" aria-hidden="true"></i>
      </span>
    </button>

    <!-- Chat panel -->
    <div v-if="isOpen" class="ai-chat-panel">
      <div class="ai-chat-header">
        <div class="ai-chat-header-title">
          <span class="icon mr-2"><i class="fas fa-robot"></i></span>
          <span>AI-suunnitteluavustaja</span>
        </div>
        <button class="ai-close-btn" @click="isOpen = false">✕</button>
      </div>

      <div class="ai-chat-messages" ref="messagesContainer">
        <!-- Welcome message -->
        <div class="ai-message ai-welcome" v-if="messages.length === 0">
          <div class="ai-message-bubble ai-bot">
            <p><strong>Hei!</strong> Olen suunnitteluavustajasi. 🎨☕</p>
            <p class="mt-2">Kuvaile suunnittelua tai valitse pohja:</p>

            <div class="ai-presets mt-2">
              <button v-for="preset in presets" :key="preset.id"
                class="ai-preset-btn" @click="selectPreset(preset)">
                <span class="ai-preset-icon">{{ preset.icon }}</span>
                <span>{{ preset.label }}</span>
              </button>
            </div>

            <p class="mt-2" style="font-size: 12px; color: #888;">💡 Liitä kuvia 📎-painikkeella — poimin värit ja käytän niitä!</p>
          </div>
        </div>

        <!-- Chat messages -->
        <div
          v-for="(msg, index) in messages"
          :key="index"
          class="ai-message"
          :class="{ 'ai-user': msg.role === 'user', 'ai-bot-msg': msg.role === 'assistant' }"
        >
          <div class="ai-message-bubble" :class="msg.role === 'user' ? 'ai-user-bubble' : 'ai-bot'">
            <div v-if="msg.images && msg.images.length" class="ai-msg-images">
              <img v-for="(img, i) in msg.images" :key="i" :src="img" class="ai-msg-image" alt="Attached" />
            </div>
            <p>{{ msg.text }}</p>
          </div>
        </div>

        <!-- Color confirmation step -->
        <div class="ai-message ai-bot-msg" v-if="colorConfirmation">
          <div class="ai-message-bubble ai-bot">
            <p><strong>🎨 Kuvasta tunnistetut värit:</strong></p>
            <p class="mt-1" style="font-size: 12px; color: #666;">Klikkaa valitaksesi: <strong>1. klikkaus</strong> = pääväri, <strong>2. klikkaus</strong> = toissijainen</p>
            <div class="ai-color-swatches mt-2">
              <button
                v-for="(color, idx) in colorConfirmation.extractedColors"
                :key="idx"
                class="ai-color-swatch"
                :class="{
                  'ai-swatch-primary': colorConfirmation.selectedPrimary === color,
                  'ai-swatch-secondary': colorConfirmation.selectedSecondary === color
                }"
                :style="{ backgroundColor: color }"
                @click="selectColor(color)"
                :title="color"
              >
                <span v-if="colorConfirmation.selectedPrimary === color" class="ai-swatch-label">1</span>
                <span v-else-if="colorConfirmation.selectedSecondary === color" class="ai-swatch-label">2</span>
              </button>
            </div>
            <div class="ai-color-legend mt-2" v-if="colorConfirmation.selectedPrimary || colorConfirmation.selectedSecondary">
              <span v-if="colorConfirmation.selectedPrimary" class="ai-color-tag">
                <span class="ai-color-dot" :style="{ backgroundColor: colorConfirmation.selectedPrimary }"></span>
                Pääväri
              </span>
              <span v-if="colorConfirmation.selectedSecondary" class="ai-color-tag">
                <span class="ai-color-dot" :style="{ backgroundColor: colorConfirmation.selectedSecondary }"></span>
                Toissijainen
              </span>
            </div>
            <div class="ai-color-actions mt-2">
              <button class="ai-color-confirm-btn" @click="confirmColorsAndSend" :disabled="!colorConfirmation.selectedPrimary">
                ✓ Vahvista värit
              </button>
              <button class="ai-color-skip-btn" @click="skipColorsAndSend">
                Ohita (auto)
              </button>
              <button v-if="hasExistingBackground" class="ai-color-skip-btn" @click="sendWithoutColors">
                🖼️ Lisää vain logona
              </button>
            </div>
            <p v-if="hasExistingBackground" class="mt-2" style="font-size: 11px; color: #999;">
              💡 "Lisää vain logona" säilyttää nykyisen taustan ja värit
            </p>
          </div>
        </div>

        <!-- Loading -->
        <div class="ai-message ai-bot-msg" v-if="isLoading">
          <div class="ai-message-bubble ai-bot ai-loading">
            <span class="ai-dot"></span>
            <span class="ai-dot"></span>
            <span class="ai-dot"></span>
          </div>
        </div>
      </div>

      <div class="ai-chat-input-wrapper">
        <!-- Image previews -->
        <div v-if="attachedImages.length" class="ai-image-previews">
          <div v-for="(img, index) in attachedImages" :key="index" class="ai-image-preview-item">
            <img :src="img" alt="Attached" />
            <button class="ai-image-remove" @click="removeImage(index)">✕</button>
          </div>
        </div>
        <div class="ai-chat-input">
          <button class="button ai-attach-btn" :disabled="isLoading" @click="triggerFileUpload" title="Liitä kuvia">
            <span class="icon"><i class="fas fa-paperclip"></i></span>
          </button>
          <input type="file" ref="fileInput" accept="image/*" multiple style="display: none" @change="handleFileSelect" />
          <input
            type="text" v-model="userInput" :disabled="isLoading"
            @keyup.enter="sendMessage" placeholder="Kuvaile suunnitteluasi..."
            class="input" ref="chatInput"
          />
          <button class="button ai-send-btn" :disabled="isLoading || (!userInput.trim() && !attachedImages.length)" @click="sendMessage">
            <span class="icon"><i class="fas fa-paper-plane"></i></span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { executeAiCommands, extractColors } from '@/services/aiDesignService'

export default {
  name: 'AiDesignChat',
  props: {
    fabricComponent: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isOpen: false,
      isLoading: false,
      userInput: '',
      messages: [],
      attachedImages: [],
      colorConfirmation: null,
      pendingRequest: null,
      presets: [
        { id: 'corporate', icon: '🏢', label: 'Yritys' },
        { id: 'eco', icon: '🌿', label: 'Eko & Luonto' },
        { id: 'festive', icon: '🎄', label: 'Juhla' },
        { id: 'minimalist', icon: '⬜', label: 'Minimalistinen' },
        { id: 'premium', icon: '✨', label: 'Premium' },
        { id: 'playful', icon: '🎨', label: 'Leikkisä' },
      ]
    }
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.chatInput) this.$refs.chatInput.focus()
        })
      }
    }
  },
  methods: {
    ...mapActions(['postAiDesign']),

    triggerFileUpload() {
      this.$refs.fileInput.click()
    },

    handleFileSelect(event) {
      Array.from(event.target.files).forEach(file => {
        if (!file.type.startsWith('image/')) return
        const reader = new FileReader()
        reader.onload = (e) => this.attachedImages.push(e.target.result)
        reader.readAsDataURL(file)
      })
      event.target.value = ''
    },

    removeImage(index) {
      this.attachedImages.splice(index, 1)
    },

    selectPreset(preset) {
      this.userInput = `Luo kokonainen ${preset.label}-tyylinen kahvikupin suunnittelu alusta alkaen`
      this.sendMessage()
    },

    /**
     * Extract a summary of the current canvas state for the AI
     */
    getCanvasState() {
      if (!this.fabricComponent || !this.fabricComponent.canvas) return null

      const canvas = this.fabricComponent.canvas
      const objects = canvas.getObjects()

      const state = {
        backgroundColor: canvas.backgroundColor || 'white',
        hasBackgroundImage: !!canvas.backgroundImage,
        elements: []
      }

      objects.forEach(obj => {
        if (obj.id === 'turtle') return // skip the turtle logo
        if (obj._isGradientBg) return // skip gradient background rects
        if (obj.type === 'i-text' || obj.type === 'text') {
          state.elements.push({
            type: 'text',
            text: obj.text,
            fontSize: obj.fontSize,
            fontFamily: obj.fontFamily,
            color: obj.fill,
            x: Math.round(obj.left),
            y: Math.round(obj.top),
            opacity: obj.opacity
          })
        } else if (obj.type === 'textbox') {
          state.elements.push({
            type: 'textbox',
            text: obj.text,
            fontSize: obj.fontSize,
            fontFamily: obj.fontFamily,
            color: obj.fill,
            width: Math.round(obj.width),
            x: Math.round(obj.left),
            y: Math.round(obj.top),
            opacity: obj.opacity
          })
        } else if (obj.type === 'circle') {
          state.elements.push({
            type: 'circle',
            radius: obj.radius,
            stroke: obj.stroke,
            fill: obj.fill,
            x: Math.round(obj.left),
            y: Math.round(obj.top),
            opacity: obj.opacity
          })
        } else if (obj.type === 'rect') {
          state.elements.push({
            type: 'rectangle',
            width: obj.width,
            height: obj.height,
            stroke: obj.stroke,
            fill: obj.fill,
            x: Math.round(obj.left),
            y: Math.round(obj.top),
            opacity: obj.opacity
          })
        } else if (obj.type === 'triangle') {
          state.elements.push({
            type: 'triangle',
            width: obj.width,
            height: obj.height,
            stroke: obj.stroke,
            fill: obj.fill,
            angle: obj.angle,
            x: Math.round(obj.left),
            y: Math.round(obj.top),
            opacity: obj.opacity
          })
        } else if (obj.type === 'ellipse') {
          state.elements.push({
            type: 'ellipse',
            rx: obj.rx,
            ry: obj.ry,
            stroke: obj.stroke,
            fill: obj.fill,
            x: Math.round(obj.left),
            y: Math.round(obj.top),
            opacity: obj.opacity
          })
        } else if (obj.type === 'line') {
          state.elements.push({
            type: 'line',
            x1: Math.round(obj.x1),
            y1: Math.round(obj.y1),
            x2: Math.round(obj.x2),
            y2: Math.round(obj.y2),
            stroke: obj.stroke,
            strokeWidth: obj.strokeWidth,
            opacity: obj.opacity
          })
        } else if (obj.type === 'polygon') {
          state.elements.push({
            type: 'polygon',
            stroke: obj.stroke,
            fill: obj.fill,
            x: Math.round(obj.left),
            y: Math.round(obj.top),
            opacity: obj.opacity
          })
        } else if (obj.type === 'image') {
          state.elements.push({
            type: 'image',
            x: Math.round(obj.left),
            y: Math.round(obj.top),
            width: Math.round(obj.width * (obj.scaleX || 1)),
            height: Math.round(obj.height * (obj.scaleY || 1)),
            opacity: obj.opacity
          })
        }
      })

      return state
    },

    selectColor(color) {
      if (!this.colorConfirmation) return
      if (this.colorConfirmation.selectedPrimary === color) {
        // Deselect primary
        this.colorConfirmation.selectedPrimary = null
        return
      }
      if (this.colorConfirmation.selectedSecondary === color) {
        // Deselect secondary
        this.colorConfirmation.selectedSecondary = null
        return
      }
      if (!this.colorConfirmation.selectedPrimary) {
        this.colorConfirmation.selectedPrimary = color
      } else if (!this.colorConfirmation.selectedSecondary) {
        this.colorConfirmation.selectedSecondary = color
      } else {
        // Both selected, replace secondary
        this.colorConfirmation.selectedSecondary = color
      }
    },

    async confirmColorsAndSend() {
      if (!this.pendingRequest || !this.colorConfirmation) return
      const { aiPrompt: basePrompt, imageDataArray, numImages } = this.pendingRequest
      const primary = this.colorConfirmation.selectedPrimary
      const secondary = this.colorConfirmation.selectedSecondary
      const palette = this.colorConfirmation.extractedColors

      let aiPrompt = basePrompt
      const parts = []
      if (primary) parts.push(`primary=${primary}`)
      if (secondary) parts.push(`secondary=${secondary}`)
      if (palette.length > 0) parts.push(`palette=${palette.join(',')}`)
      if (parts.length > 0) {
        aiPrompt += ` [BRAND_COLORS: ${parts.join(', ')}]`
      }

      this.colorConfirmation = null
      this.isLoading = true
      this.scrollToBottom()

      await this.executeAiRequest(aiPrompt, imageDataArray, numImages > 0)
    },

    async skipColorsAndSend() {
      if (!this.pendingRequest) return
      const { aiPrompt, imageDataArray, numImages, autoColors } = this.pendingRequest

      let finalPrompt = aiPrompt
      // Use auto-detected colors
      if (autoColors) {
        const parts = []
        if (autoColors.primary) parts.push(`primary=${autoColors.primary}`)
        if (autoColors.secondary) parts.push(`secondary=${autoColors.secondary}`)
        if (autoColors.all.length > 0) parts.push(`palette=${autoColors.all.join(',')}`)
        if (parts.length > 0) {
          finalPrompt += ` [BRAND_COLORS: ${parts.join(', ')}]`
        }
      }

      this.colorConfirmation = null
      this.isLoading = true
      this.scrollToBottom()

      await this.executeAiRequest(finalPrompt, imageDataArray, numImages > 0)
    },

    async sendWithoutColors() {
      if (!this.pendingRequest) return
      const { aiPrompt, imageDataArray, numImages } = this.pendingRequest

      let finalPrompt = aiPrompt + ' [LOGO_ONLY: true, DO NOT change background color or background image, only add the attached image as a logo element]'

      this.colorConfirmation = null
      this.isLoading = true
      this.scrollToBottom()

      await this.executeAiRequest(finalPrompt, imageDataArray, numImages > 0)
    },

    async sendMessage() {
      const prompt = this.userInput.trim()
      const hasImages = this.attachedImages.length > 0
      if (!prompt && !hasImages) return
      if (this.isLoading || this.colorConfirmation) return

      const imageDataArray = [...this.attachedImages]
      const numImages = imageDataArray.length

      this.messages.push({
        role: 'user',
        text: prompt || `(${numImages} kuva${numImages > 1 ? 'a' : ''} liitetty)`,
        images: imageDataArray.length ? imageDataArray : null
      })
      this.userInput = ''
      this.attachedImages = []
      this.scrollToBottom()

      let aiPrompt = prompt || 'Use the attached image(s) in the design'

      // Add canvas dimensions
      if (this.fabricComponent) {
        const w = this.fabricComponent.canvasWidth || 1000
        const h = this.fabricComponent.canvasHeight || 400
        aiPrompt += ` [CANVAS: ${w}x${h}]`
      }

      // Add canvas state
      const canvasState = this.getCanvasState()
      if (canvasState) {
        const stateStr = JSON.stringify(canvasState)
        aiPrompt += ` [CANVAS_STATE: ${stateStr}]`
      }

      // If images attached, extract colors and show confirmation
      if (hasImages) {
        this.isLoading = true
        aiPrompt += ` [ATTACHED_IMAGES: ${numImages}]`

        try {
          const colorResults = await Promise.all(
            imageDataArray.map(img => extractColors(img, 6))
          )

          // Merge all brand colors from all images
          const allBrandColors = []
          let autoDetected = { primary: null, secondary: null, all: [] }
          colorResults.forEach(result => {
            result.all.forEach(c => {
              if (!allBrandColors.includes(c)) allBrandColors.push(c)
            })
            if (!autoDetected.primary && result.primary) autoDetected.primary = result.primary
            if (!autoDetected.secondary && result.secondary) autoDetected.secondary = result.secondary
          })
          autoDetected.all = allBrandColors

          this.isLoading = false

          if (allBrandColors.length > 0) {
            // Show color confirmation UI
            this.pendingRequest = {
              aiPrompt,
              imageDataArray,
              numImages,
              autoColors: autoDetected
            }
            this.colorConfirmation = {
              extractedColors: allBrandColors.slice(0, 8),
              selectedPrimary: autoDetected.primary,
              selectedSecondary: autoDetected.secondary
            }
            this.scrollToBottom()
            return // Wait for user to confirm
          }
        } catch (err) {
          console.error('Color extraction error:', err)
          this.isLoading = false
        }
      }

      // No images or color extraction failed — send directly
      this.isLoading = true

      await this.executeAiRequest(aiPrompt, imageDataArray, hasImages)
    },

    async executeAiRequest(aiPrompt, imageDataArray, hasImages) {
      try {
        const response = await this.postAiDesign({ prompt: aiPrompt })

        if (response && response.data) {
          const { commands, message } = response.data

          this.messages.push({
            role: 'assistant',
            text: message || 'Suunnittelu toteutettu.'
          })

          if (commands && commands.length > 0 && this.fabricComponent) {
            if (hasImages && imageDataArray && imageDataArray.length > 0) {
              commands.forEach(cmd => {
                if (cmd.action === 'addImage' || cmd.action === 'setBackgroundImage') {
                  cmd.params = cmd.params || {}
                  const idx = cmd.params.imageIndex || 0
                  cmd.params.imageData = imageDataArray[Math.min(idx, imageDataArray.length - 1)]
                }
              })
            }
            executeAiCommands(commands, this.fabricComponent)
          }
        } else {
          this.messages.push({
            role: 'assistant',
            text: 'Pyynnön käsittelyssä tapahtui virhe. Yritä uudelleen.'
          })
        }
      } catch (error) {
        console.error('AI Design error:', error)
        this.messages.push({
          role: 'assistant',
          text: 'Yhteysvirhe avustajaan. Tarkista, että palvelin on käynnissä.'
        })
      } finally {
        this.isLoading = false
        this.pendingRequest = null
        this.scrollToBottom()
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) container.scrollTop = container.scrollHeight
      })
    }
  },
  computed: {
    hasExistingBackground() {
      if (!this.fabricComponent) return false
      const canvas = this.fabricComponent.canvas
      if (!canvas) return false
      return !!(canvas.backgroundImage || (canvas.backgroundColor && canvas.backgroundColor !== 'white' && canvas.backgroundColor !== '#ffffff'))
    }
  }
}
</script>

<style scoped lang="scss">
@use '../assets/sass/variables' as *;

.ai-design-chat-wrapper {
  font-family: Ubuntu, Helvetica, sans-serif;
}

.ai-toggle-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, $pyroll-violet, lighten($pyroll-violet, 10%));
  color: white;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(76, 65, 132, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 1002;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(76, 65, 132, 0.6);
  }

  .icon { font-size: 20px; }
}

.ai-chat-panel {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 380px;
  max-height: 580px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1001;
  animation: slideUp 0.3s ease;

  @media (max-width: 768px) {
    bottom: 0;
    right: 0;
    width: 100%;
    max-height: 70vh;
    border-radius: 16px 16px 0 0;
  }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.ai-chat-header {
  background: linear-gradient(135deg, $pyroll-violet, lighten($pyroll-violet, 10%));
  color: white;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;

  .ai-chat-header-title {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 15px;
  }
}

.ai-close-btn {
  background: rgba(255, 255, 255, 0.3);
  border: none;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  &:hover { background: rgba(255, 255, 255, 0.5); }
}

.ai-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  min-height: 250px;
  max-height: 360px;
  background: #F8F9FA;
}

.ai-message {
  margin-bottom: 12px;
  display: flex;
  &.ai-user { justify-content: flex-end; }
  &.ai-bot-msg { justify-content: flex-start; }
}

.ai-message-bubble {
  max-width: 85%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.5;

  &.ai-user-bubble {
    background: linear-gradient(135deg, $pyroll-violet, lighten($pyroll-violet, 10%));
    color: white;
    border-bottom-right-radius: 4px;
  }

  &.ai-bot {
    background: white;
    color: #333;
    border: 1px solid #E8E8E8;
    border-bottom-left-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  p { text-align: left; font-size: 14px; margin: 0; }
}

.ai-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ai-preset-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: 1.5px solid darken($pyroll-green, 5%);
  border-radius: 20px;
  background: lighten($pyroll-green, 42%);
  color: darken($pyroll-green, 22%);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  .ai-preset-icon { font-size: 14px; }

  &:hover {
    background: lighten($pyroll-green, 35%);
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(38, 208, 124, 0.2);
  }
}

.ai-msg-images {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 6px;
}

.ai-msg-image {
  max-width: 80px;
  max-height: 80px;
  border-radius: 8px;
  display: block;
  object-fit: cover;
}

.ai-image-previews {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 16px 4px;
}

.ai-image-preview-item {
  position: relative;

  img {
    max-height: 50px;
    max-width: 60px;
    border-radius: 6px;
    border: 2px solid $pyroll-green;
    object-fit: cover;
  }

  .ai-image-remove {
    position: absolute;
    top: -6px;
    right: -6px;
    background: #E74C3C;
    color: white;
    border: none;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    font-size: 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover { background: darken(#E74C3C, 10%); }
  }
}

.ai-loading {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 14px 20px;
}

.ai-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: $pyroll-green;
  animation: bounce 1.4s infinite ease-in-out both;
  &:nth-child(1) { animation-delay: -0.32s; }
  &:nth-child(2) { animation-delay: -0.16s; }
  &:nth-child(3) { animation-delay: 0s; }
}

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
}

.ai-chat-input-wrapper {
  border-top: 1px solid #E8E8E8;
  background: white;
  flex-shrink: 0;
}

.ai-chat-input {
  display: flex;
  gap: 8px;
  padding: 12px 16px;

  .input {
    flex: 1;
    border-radius: 20px;
    border: 2px solid #E0E0E0;
    padding: 8px 16px;
    font-size: 14px;
    transition: border-color 0.2s;
    &:focus {
      border-color: $pyroll-violet;
      box-shadow: 0 0 0 3px rgba(76, 65, 132, 0.1);
      outline: none;
    }
  }

  .ai-attach-btn {
    border-radius: 50%;
    width: 40px;
    height: 40px;
    min-width: 40px;
    padding: 0;
    border: 2px solid #E0E0E0;
    background: white;
    color: $pyroll-violet;
    cursor: pointer;
    transition: all 0.2s;
    &:hover:not(:disabled) {
      border-color: $pyroll-violet;
      background: lighten($pyroll-violet, 45%);
    }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }

  .ai-send-btn {
    border-radius: 50%;
    width: 40px;
    height: 40px;
    min-width: 40px;
    padding: 0;
    border: none;
    background: $pyroll-green;
    color: white;
    cursor: pointer;
    transition: all 0.2s;
    &:hover:not(:disabled) {
      transform: scale(1.05);
      box-shadow: 0 2px 8px rgba(38, 208, 124, 0.4);
      background: darken($pyroll-green, 5%);
    }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }
}

// Color confirmation step
.ai-color-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ai-color-swatch {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: 3px solid #E0E0E0;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  &.ai-swatch-primary {
    border-color: $pyroll-violet;
    box-shadow: 0 0 0 2px $pyroll-violet;
    transform: scale(1.1);
  }

  &.ai-swatch-secondary {
    border-color: $pyroll-green;
    box-shadow: 0 0 0 2px $pyroll-green;
    transform: scale(1.1);
  }
}

.ai-swatch-label {
  font-size: 11px;
  font-weight: 700;
  color: white;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
}

.ai-color-legend {
  display: flex;
  gap: 12px;
  font-size: 12px;
}

.ai-color-tag {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #555;
}

.ai-color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  border: 1px solid rgba(0, 0, 0, 0.15);
}

.ai-color-actions {
  display: flex;
  gap: 8px;
}

.ai-color-confirm-btn {
  padding: 6px 16px;
  border-radius: 16px;
  border: none;
  background: $pyroll-green;
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:hover:not(:disabled) {
    background: darken($pyroll-green, 5%);
    transform: translateY(-1px);
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
}

.ai-color-skip-btn {
  padding: 6px 16px;
  border-radius: 16px;
  border: 1.5px solid #CCC;
  background: white;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    border-color: #999;
    background: #F5F5F5;
  }
}
</style>
